import { listOrgEnrollments } from "./enrollmentHelpers"
import { listCourses } from "./courseHelpers"
import { listOrgCertificates } from "./certHelpers"
import { toMillis } from "./dateRangeHelpers"
import { ACTIVE_WITHIN_DAYS, AT_RISK_WITHIN_DAYS } from "./learnerHealthHelpers"

const DAY_MS = 86400000

// Builds one row per distinct learner (userId) across all their enrollments in
// this org — the source of truth for the Learners table. There's no separate
// learners collection; this aggregates the same enrollments
// computeLearnerHealthBreakdown() counts, but keeps per-learner detail instead
// of just totals.
export const listOrgLearners = async (orgId) => {
  const [{ enrollments }, { courses }, { certificates }] = await Promise.all([
    listOrgEnrollments(orgId),
    listCourses(orgId),
    listOrgCertificates(orgId),
  ])

  const courseTitleById = {}
  courses?.forEach((c) => {
    courseTitleById[c.courseId] = c.title
  })

  const byUser = {}
  enrollments?.forEach((e) => {
    const uid = e?.userId
    if (!uid) return
    if (!byUser[uid]) {
      byUser[uid] = {
        userId: uid,
        name: "",
        email: "",
        avatarUrl: "",
        nesolearnId: "",
        enrollments: [],
        earliestEnrolledAtMs: null,
      }
    }
    const bucket = byUser[uid]
    // Prefer the first non-empty identity fields found across this learner's
    // enrollments — older enrollments (pre-identity-denormalization) may be blank
    // while a newer one for the same learner has them filled in.
    if (!bucket.name && e?.learnerName) bucket.name = e.learnerName
    if (!bucket.email && e?.learnerEmail) bucket.email = e.learnerEmail
    if (!bucket.avatarUrl && e?.learnerAvatarUrl) bucket.avatarUrl = e.learnerAvatarUrl
    if (!bucket.nesolearnId && e?.learnerNesolearnId) bucket.nesolearnId = e.learnerNesolearnId

    bucket.enrollments.push(e)

    const enrolledMs = toMillis(e?.enrolledAt)
    if (enrolledMs !== null && (bucket.earliestEnrolledAtMs === null || enrolledMs < bucket.earliestEnrolledAtMs)) {
      bucket.earliestEnrolledAtMs = enrolledMs
    }
  })

  const now = Date.now()

  const learners = Object.values(byUser).map((bucket) => {
    const lastActivityMs = bucket.enrollments.reduce((max, e) => {
      const ms = toMillis(e?.lastActivityAt)
      return ms !== null && ms > max ? ms : max
    }, 0)

    const daysSince = lastActivityMs ? (now - lastActivityMs) / DAY_MS : Infinity
    const status =
      daysSince <= ACTIVE_WITHIN_DAYS ? "active" : daysSince <= AT_RISK_WITHIN_DAYS ? "at-risk" : "inactive"

    const coursesCount = bucket.enrollments.length
    const completedCount = bucket.enrollments.filter((e) => (e?.progressPercent || 0) >= 100).length
    const avgProgress =
      coursesCount > 0
        ? Math.round(bucket.enrollments.reduce((sum, e) => sum + (e?.progressPercent || 0), 0) / coursesCount)
        : 0

    const credentialsCount = certificates?.filter((c) => c?.userId === bucket.userId)?.length || 0
    const courseTitles = bucket.enrollments.map((e) => courseTitleById[e?.courseId] || "Untitled Course")

    return {
      userId: bucket.userId,
      name: bucket.name,
      email: bucket.email,
      avatarUrl: bucket.avatarUrl,
      nesolearnId: bucket.nesolearnId,
      avgProgress,
      coursesCount,
      completedCount,
      credentialsCount,
      courseTitles,
      lastActivityMs: lastActivityMs || null,
      enrolledAtMs: bucket.earliestEnrolledAtMs,
      status,
      enrollments: bucket.enrollments,
    }
  })

  return { learners, error: null }
}

// Top metric strip: Active / New (enrolled in the last 30 days) / Completed
// (at least one enrollment at 100%) / At Risk / Inactive. Active/At-Risk/
// Inactive are mutually exclusive (every learner is exactly one); New and
// Completed are separate cross-cutting counts layered on top — same
// convention KPIRow already uses for mixing totals with rates.
export const computeLearnerMetrics = (learners) => {
  const now = Date.now()
  const total = learners?.length || 0
  const active = learners?.filter((l) => l.status === "active")?.length || 0
  const atRisk = learners?.filter((l) => l.status === "at-risk")?.length || 0
  const inactive = learners?.filter((l) => l.status === "inactive")?.length || 0
  const newCount = learners?.filter((l) => l.enrolledAtMs && now - l.enrolledAtMs <= 30 * DAY_MS)?.length || 0
  const completed = learners?.filter((l) => l.completedCount > 0)?.length || 0

  return { total, active, newCount, completed, atRisk, inactive }
}
